import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Skeleton, alpha, useTheme } from '@mui/material';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  color?: string;
  loading?: boolean;
  hint?: string;
  trend?: { value: number; label?: string };
  /** When set, the card navigates to this route on click. */
  to?: string;
}

export default function StatCard({ title, value, icon, color, loading, hint, trend, to }: StatCardProps) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const accent = color || theme.palette.primary.main;
  const clickable = Boolean(to);

  return (
    <Card
      component={clickable ? RouterLink : 'div'}
      to={clickable ? to : undefined}
      role={clickable ? 'link' : undefined}
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isLight
            ? `0 12px 32px rgba(15,23,42,0.1), 0 0 0 1px ${alpha(accent, 0.15)}`
            : `0 12px 32px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(accent, 0.25)}`,
        },
        '&:focus-visible': clickable ? {
          outline: `2px solid ${alpha(accent, 0.55)}`,
          outlineOffset: 2,
        } : undefined,
      }}
    >
      {/* Subtle top accent bar */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.4)})`,
        borderRadius: '16px 16px 0 0',
      }} />
      <CardContent sx={{ p: 2.5, pt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Box minWidth={0} flex={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              noWrap
              sx={{ fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={100} height={40} sx={{ borderRadius: '8px' }} />
            ) : (
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  letterSpacing: '-0.035em',
                  fontSize: { xs: '1.55rem', sm: '1.85rem' },
                  color: 'text.primary',
                }}
              >
                {value}
              </Typography>
            )}
            {hint && !loading && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ fontSize: '0.75rem' }}>
                {hint}
              </Typography>
            )}
            {trend && !loading && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.75,
                  px: 1,
                  py: 0.35,
                  borderRadius: '6px',
                  bgcolor: trend.value >= 0 ? alpha('#059669', 0.1) : alpha('#dc2626', 0.1),
                  color: trend.value >= 0 ? '#059669' : '#dc2626',
                }}
              >
                <Typography variant="caption" fontWeight={700} fontSize="0.72rem">
                  {trend.value >= 0 ? '+' : ''}{trend.value}%
                </Typography>
                {trend.label && (
                  <Typography variant="caption" fontWeight={500} fontSize="0.68rem" sx={{ opacity: 0.8 }}>
                    {trend.label}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '14px',
              background: `linear-gradient(135deg, ${alpha(accent, 0.15)} 0%, ${alpha(accent, 0.08)} 100%)`,
              border: `1px solid ${alpha(accent, 0.18)}`,
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: '1.4rem' },
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
